/**
 * FamilyAccess — patient-controlled, consent-scoped family / caregiver access.
 *
 * Shown on the patient Profile "Health record" tab. Only the patient themselves
 * (self) or an ADMIN may view or mutate grants — the parent page guards this,
 * and the component also fails safe if `canManage` is false.
 *
 * Family members receive READ-ONLY access to the scopes the patient selects
 * (visits, clinical summary). The patient can revoke any grant at any time.
 *
 * Endpoints (backend):
 *   GET    /patients/{id}/family-access            → [{ grant_id, family_name,
 *            family_party_id, relationship, scopes[], granted_at }]
 *   POST   /patients/{id}/family-access            → create a grant
 *   DELETE /patients/{id}/family-access/{grantId}  → revoke a grant
 *
 * Response shapes are tolerant: list may arrive as a bare array or {data:[...]}.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { familyAccessAPI } from '../../services/api';
import { COLOR } from '../../components/ss/tokens';

const BP = {
  primary: COLOR.primary, navy: COLOR.navy, danger: COLOR.red, muted: '#8b7681',
  border: '#ECE3E7', soft: '#f7f3f4',
  font: "'Inter', -apple-system, 'Segoe UI', sans-serif",
  gradBtn: 'linear-gradient(135deg, #7b1e3c, #94335A)',
};

const RELATIONSHIPS = [
  { id: 'CAREGIVER_OF', label: 'Caregiver' },
  { id: 'FAMILY_MEMBER', label: 'Family member' },
];

const SCOPES = [
  { id: 'VISITS', label: 'Visits', hint: 'Upcoming & past visit schedule' },
  { id: 'CLINICAL_SUMMARY', label: 'Clinical summary', hint: 'Care summary & clinical highlights' },
];

const relationshipLabel = (id) =>
  RELATIONSHIPS.find(r => r.id === id)?.label || String(id || '—').replace(/_/g, ' ');

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};

// Tolerate both bare arrays and { success, data } envelopes.
const asList = (v) => (Array.isArray(v) ? v : Array.isArray(v?.data) ? v.data : []);

const errText = (e) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join('; ');
  return e?.message || 'Something went wrong.';
};

const Section = ({ children }) => (
  <div style={{ background: '#fff', border: `1px solid ${BP.border}`, borderRadius: 14,
    padding: 18, marginBottom: 14, fontFamily: BP.font }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
      <span style={{ fontSize: 14 }}>👪</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: BP.navy }}>Family &amp; caregiver access</span>
    </div>
    {children}
  </div>
);

const Toast = ({ toast, onClose }) => {
  if (!toast) return null;
  const err = toast.kind === 'error';
  return (
    <div style={{
      background: err ? COLOR.redBg : COLOR.greenBg,
      border: `1px solid ${err ? COLOR.redBorder : COLOR.greenBorder}`,
      color: err ? COLOR.redDark : COLOR.greenDark,
      borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 12.5, fontWeight: 600,
      display: 'flex', alignItems: 'center', gap: 8, fontFamily: BP.font,
    }}>
      <span>{err ? '⚠️' : '✅'}</span>
      <span style={{ flex: 1 }}>{toast.text}</span>
      <button onClick={onClose}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
    </div>
  );
};

const Skeleton = () => (
  <div style={{ fontFamily: BP.font }}>
    {[0, 1].map(i => (
      <div key={i} style={{ border: `1px solid ${BP.border}`, borderRadius: 12,
        padding: '14px 16px', marginBottom: 10, background: BP.soft, opacity: 1 - i * 0.25 }}>
        <div style={{ height: 12, width: '40%', background: '#e7dde2', borderRadius: 6, marginBottom: 8 }} />
        <div style={{ height: 10, width: '65%', background: '#efe7eb', borderRadius: 6 }} />
      </div>
    ))}
  </div>
);

const Chip = ({ children }) => (
  <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999,
    fontSize: 11.5, fontWeight: 600, fontFamily: BP.font, background: COLOR.blueBg,
    color: COLOR.blue, border: `1px solid ${COLOR.blueBorder}`, marginRight: 6, marginBottom: 4 }}>
    {children}
  </span>
);

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '9px 12px', fontSize: 13,
  fontFamily: BP.font, color: BP.navy, background: '#fff', borderRadius: 10,
  border: `1.5px solid ${BP.border}`, outline: 'none',
};

const fieldLabel = { display: 'block', fontSize: 12, fontWeight: 600, color: BP.navy, marginBottom: 5 };

export default function FamilyAccess({ patientId, canManage = false }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState(null);
  const [busyId, setBusyId] = useState(null);   // grant being revoked
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    member: '',
    relationship: 'CAREGIVER_OF',
    scopes: ['VISITS'],
  });

  const showToast = (kind, text) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 5000);
  };

  const load = useCallback((alive = { current: true }) => {
    if (!patientId || !canManage) { setLoading(false); return; }
    setLoading(true); setLoadError('');
    familyAccessAPI.list(patientId)
      .then(d => { if (alive.current) setGrants(asList(d)); })
      .catch(e => { if (alive.current) setLoadError(errText(e)); })
      .finally(() => { if (alive.current) setLoading(false); });
  }, [patientId, canManage]);

  useEffect(() => {
    const alive = { current: true };
    load(alive);
    return () => { alive.current = false; };
  }, [load]);

  if (!canManage) return null;

  const toggleScope = (id) =>
    setForm(f => ({
      ...f,
      scopes: f.scopes.includes(id) ? f.scopes.filter(s => s !== id) : [...f.scopes, id],
    }));

  const grant = async () => {
    const member = form.member.trim();
    if (!member) { showToast('error', 'Enter a family member (party ID or email).'); return; }
    if (form.scopes.length === 0) { showToast('error', 'Select at least one scope.'); return; }
    setSubmitting(true);
    // Send both keys so the backend can accept party id OR email; an email is
    // detected by the presence of "@".
    const isEmail = member.includes('@');
    const payload = {
      relationship: form.relationship,
      scopes: form.scopes,
      // Backend FamilyAccessGrant expects family_party_id | email.
      ...(isEmail ? { email: member } : { family_party_id: member }),
    };
    try {
      await familyAccessAPI.grant(patientId, payload);
      showToast('success', 'Access granted.');
      setForm({ member: '', relationship: 'CAREGIVER_OF', scopes: ['VISITS'] });
      load();
    } catch (e) {
      showToast('error', errText(e));
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (g) => {
    if (busyId) return;
    if (!window.confirm(`Revoke access for ${g.family_name || 'this person'}? They will lose access immediately.`)) return;
    setBusyId(g.grant_id);
    try {
      await familyAccessAPI.revoke(patientId, g.grant_id);
      showToast('success', 'Access revoked.');
      load();
    } catch (e) {
      showToast('error', errText(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Section>
      {/* Read-only framing */}
      <p style={{ fontSize: 12.5, color: BP.muted, margin: '0 0 14px', lineHeight: 1.6, fontFamily: BP.font }}>
        Give a family member or caregiver <strong>read-only</strong> access to parts of your
        record. They can only see the scopes you choose below, can never edit anything, and you
        can revoke their access at any time.
      </p>

      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* Current grants */}
      <div style={{ fontSize: 11, fontWeight: 800, color: BP.muted, textTransform: 'uppercase',
        letterSpacing: '0.5px', marginBottom: 8, fontFamily: BP.font }}>
        People with access
      </div>

      {loading ? (
        <Skeleton />
      ) : loadError ? (
        <div style={{ background: COLOR.redBg, border: `1px solid ${COLOR.redBorder}`, color: COLOR.redDark,
          borderRadius: 10, padding: '10px 14px', fontSize: 12.5, fontFamily: BP.font, marginBottom: 12 }}>
          ⚠️ {loadError}
        </div>
      ) : grants.length === 0 ? (
        <div style={{ padding: '14px 12px', borderRadius: 10, background: BP.soft,
          fontSize: 12.5, color: BP.muted, fontFamily: BP.font, marginBottom: 12 }}>
          No one has access to your record yet. Use the form below to invite a family member or caregiver.
        </div>
      ) : (
        grants.map((g, i) => (
          <div key={g.grant_id || i} style={{ border: `1px solid ${BP.border}`, borderRadius: 12,
            padding: '12px 14px', marginBottom: 10, background: '#fff', fontFamily: BP.font,
            opacity: busyId === g.grant_id ? 0.55 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: BP.navy }}>
                {g.family_name || `Party #${String(g.family_party_id || '').slice(0, 8)}`}
              </span>
              <Chip>{relationshipLabel(g.relationship)}</Chip>
              <button type="button" onClick={() => revoke(g)} disabled={!!busyId}
                style={{ marginLeft: 'auto', background: 'transparent', border: `1.5px solid ${BP.danger}`,
                  color: BP.danger, borderRadius: 10, padding: '5px 12px', fontSize: 12, fontWeight: 700,
                  fontFamily: BP.font, cursor: busyId ? 'wait' : 'pointer', opacity: busyId ? 0.5 : 1 }}>
                {busyId === g.grant_id ? 'Revoking…' : 'Revoke'}
              </button>
            </div>
            <div style={{ marginTop: 8 }}>
              {(g.scopes || []).length === 0
                ? <span style={{ fontSize: 12, color: BP.muted }}>No scopes</span>
                : g.scopes.map(s => (
                    <Chip key={s}>{SCOPES.find(x => x.id === s)?.label || String(s).replace(/_/g, ' ')}</Chip>
                  ))}
            </div>
            <div style={{ fontSize: 11.5, color: BP.muted, marginTop: 6 }}>
              Granted {fmtDate(g.granted_at)}
            </div>
          </div>
        ))
      )}

      {/* Grant form */}
      <div style={{ borderTop: `1px solid ${BP.soft}`, marginTop: 14, paddingTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: BP.muted, textTransform: 'uppercase',
          letterSpacing: '0.5px', marginBottom: 10, fontFamily: BP.font }}>
          Grant access
        </div>

        <div style={{ maxWidth: 420 }}>
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={fieldLabel}>Family member (party ID or email)</span>
            <input value={form.member}
              onChange={e => setForm(f => ({ ...f, member: e.target.value }))}
              placeholder="e.g. 42 or jane@example.com" style={inputStyle} />
          </label>

          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={fieldLabel}>Relationship</span>
            <select value={form.relationship}
              onChange={e => setForm(f => ({ ...f, relationship: e.target.value }))}
              style={{ ...inputStyle, cursor: 'pointer' }}>
              {RELATIONSHIPS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </label>

          <div style={{ marginBottom: 14 }}>
            <span style={fieldLabel}>What they can see (read-only)</span>
            {SCOPES.map(s => (
              <label key={s.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8,
                fontSize: 13, color: BP.navy, cursor: 'pointer', fontFamily: BP.font }}>
                <input type="checkbox" checked={form.scopes.includes(s.id)}
                  onChange={() => toggleScope(s.id)}
                  style={{ width: 16, height: 16, accentColor: BP.primary, marginTop: 2 }} />
                <span>
                  <span style={{ fontWeight: 600 }}>{s.label}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: BP.muted }}>{s.hint}</span>
                </span>
              </label>
            ))}
          </div>

          <button type="button" onClick={grant} disabled={submitting}
            style={{ padding: '10px 22px', borderRadius: 10, border: 'none', background: BP.gradBtn,
              color: '#fff', fontWeight: 700, fontSize: 13.5, fontFamily: BP.font,
              boxShadow: '0 2px 10px rgba(123,30,60,.30)',
              cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.55 : 1 }}>
            {submitting ? 'Granting…' : 'Grant access'}
          </button>
        </div>
      </div>
    </Section>
  );
}
